import React, { useState, useEffect, useCallback } from 'react';
import { getAccessToken } from '../../services/authService';
import debounce from 'lodash/debounce';
import { useDispatch } from "react-redux";
import { logOutAsync } from "../../store/asyncLogout";
import { MdLogout } from "react-icons/md";
import { useNavigate } from 'react-router-dom';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const getCountryName = (countryCode) => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(countryCode) || countryCode;
  } catch (error) {
    return countryCode || '';
  }
};

const getStatusColor = (isVerified) => {
  return isVerified 
    ? 'bg-green-100 text-green-800 font-semibold'
    : 'bg-red-100 text-red-800 font-semibold';
};

const getScriptColor = (count) => {
  if (count === 0) return 'bg-gray-100 text-gray-800 font-semibold';
  if (count < 5) return 'bg-orange-100 text-orange-800 font-semibold';
  return 'bg-green-100 text-green-800 font-semibold';
};

const getPlanColor = (plan) => {
  if (!plan) return 'bg-gray-100 text-gray-800';
  switch (plan.toLowerCase()) {
    case 'free': return 'bg-[rgb(219,161,2)] text-white font-semibold';
    case 'freemium': return 'bg-[rgb(255,163,0)] text-white font-semibold';
    case 'essential': return 'bg-[rgb(2,153,253)] text-white font-semibold';
    case 'professional': return 'bg-[rgb(26,73,186)] text-white font-semibold';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getValidTillColor = (endDate) => {
  if (!endDate) return 'bg-gray-100 text-gray-800';
  
  const today = new Date();
  const end = new Date(endDate);
  const timeDiff = end - today;
  const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

  if (daysDiff < 0) {
    return 'bg-gradient-to-r from-rose-200 to-rose-400 text-rose-900 font-semibold';
  } else if (daysDiff <= 7) {
    return 'bg-gradient-to-r from-amber-200 to-orange-300 text-orange-900 font-semibold';
  } else {
    return 'bg-gradient-to-r from-emerald-200 to-emerald-300 text-emerald-900 font-semibold';
  }
};

const getAuthColor = (loginType) => {
  if (!loginType) return 'bg-gray-100 text-gray-800';
  
  switch (loginType.toLowerCase()) {
    case 'facebook':
      return 'bg-gradient-to-r from-blue-200 to-blue-300 text-blue-900 font-semibold';
    case 'google':
      return 'bg-gradient-to-r from-red-200 to-orange-200 text-red-900 font-semibold';
    case 'email':
      return 'bg-gradient-to-r from-violet-200 to-purple-200 text-violet-900 font-semibold';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const Admin = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [userData, setUserData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const itemsPerPage = 10;
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Create debounced search function
  const debouncedSearch = useCallback(
    debounce((query) => {
      setCurrentPage(1); // Reset to first page
      fetchUsers(query);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSearch(searchQuery);
    return () => debouncedSearch.cancel();
  }, [searchQuery, debouncedSearch]);

  useEffect(() => {
    fetchUsers();
  }, [currentPage]);

  const fetchUsers = async (search = searchQuery) => {
    setIsLoading(true);
    const accessToken = getAccessToken()
    try {
      if (!accessToken) {
        throw new Error("access token not found");
      }
      const skip = (currentPage - 1) * itemsPerPage;
      const response = await fetch(
        `${process.env.REACT_APP_DEV_API}/user?skip=${skip}&limit=${itemsPerPage}${search ? `&search=${search}` : ''}`,
        {
          headers: {
            'Accept': '*/*',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      
      const data = await response.json();
      setUserData(data.data.users || []);
      setTotalPages(Math.ceil(data.data.totalCount / itemsPerPage));
      setTotalUsers(data.data.totalCount);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    
    buttons.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`px-3 py-1 border rounded ${
          currentPage === 1
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white text-gray-700 hover:bg-gray-100'
        }`}
      >
        ←
      </button>
    );

    const addEllipsis = (key) => (
      <span key={`ellipsis-${key}`} className="px-3 py-1">...</span>
    );

    const addPageButton = (pageNum) => (
      <button
        key={pageNum}
        onClick={() => handlePageChange(pageNum)}
        className={`px-3 py-1 border rounded ${
          currentPage === pageNum
            ? 'bg-blue-500 text-white'
            : 'bg-white text-gray-700 hover:bg-gray-100'
        }`}
      >
        {pageNum}
      </button>
    );

    buttons.push(addPageButton(1));

    if (totalPages <= 5) {
      for (let i = 2; i <= totalPages; i++) {
        buttons.push(addPageButton(i));
      }
    } else {
      if (currentPage <= 3) {
        buttons.push(addPageButton(2));
        buttons.push(addPageButton(3));
        buttons.push(addPageButton(4));
        buttons.push(addEllipsis('end'));
        buttons.push(addPageButton(totalPages));
      } else if (currentPage >= totalPages - 2) {
        buttons.push(addEllipsis('start'));
        buttons.push(addPageButton(totalPages - 2));
        buttons.push(addPageButton(totalPages - 1));
        buttons.push(addPageButton(totalPages));
      } else {
        buttons.push(addEllipsis('start'));
        buttons.push(addPageButton(currentPage - 1));
        buttons.push(addPageButton(currentPage));
        buttons.push(addPageButton(currentPage + 1));
        buttons.push(addEllipsis('end'));
        buttons.push(addPageButton(totalPages));
      }
    }

    buttons.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`px-3 py-1 border rounded ${
          currentPage === totalPages
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white text-gray-700 hover:bg-gray-100'
        }`}
      >
        →
      </button>
    );

    return buttons;
  };

  const handleLogoutClick = () => {
    dispatch(logOutAsync());
    localStorage.removeItem("user");
    localStorage.removeItem("session");
    navigate('/login');
  };

  return (
    <div className="p-6">
      <div className="flex flex-col gap-4 mb-6">
        {/* Mobile View */}
        <div className="flex justify-between items-center md:hidden">
          <div className="relative">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-[#2153D3] to-[#4672fe] bg-clip-text text-transparent">
              Admin Console
            </h1>
            <div className="h-1 w-24 bg-gradient-to-r from-[#2153D3] to-[#4672fe] rounded-full mt-1"></div>
          </div>
          <div
            onClick={handleLogoutClick}
            className="cursor-pointer flex text-[#585858] items-center gap-2 p-4"
          >
            <MdLogout className="size-[24px] font-medium" color="#AAAAAA" />
            <p className="text-[#585858] text-[15px]">Log Out</p>
          </div>
        </div>

        {/* Desktop/Tablet View - All elements in single row */}
        <div className="hidden md:flex md:flex-row md:items-center md:justify-between w-full gap-4">
          <div className="relative">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-[#2153D3] to-[#4672fe] bg-clip-text text-transparent">
              Admin Console
            </h1>
            <div className="h-1 w-24 bg-gradient-to-r from-[#2153D3] to-[#4672fe] rounded-full mt-1"></div>
          </div>

          <div className="flex items-center gap-3">
            <div className={`relative transition-all duration-500 ease-in-out ${
              isFocused ? 'w-80' : searchQuery ? 'w-64' : 'w-48'
            }`}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder="Search users..."
                className={`w-full px-4 py-2 rounded-full border-2 transition-all duration-500 ease-in-out ${
                  isFocused
                    ? 'border-blue-500 shadow-lg'
                    : searchQuery
                    ? 'border-blue-300 shadow-md'
                    : 'border-gray-300'
                } focus:outline-none focus:ring-2 focus:ring-blue-300`}
              />
              <div className={`absolute inset-0 -z-10 transition-opacity duration-500 ease-in-out ${
                isFocused || searchQuery ? 'opacity-100' : 'opacity-0'
              }`}>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-200 to-blue-400 blur-lg rounded-full"></div>
              </div>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <svg
                  className={`w-5 h-5 text-gray-400 transition-colors duration-500 ease-in-out ${
                    isFocused || searchQuery ? 'text-blue-500' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 16l-4-4m0 0l4-4m-4 4h12"
                  ></path>
                </svg>
              </div>
            </div>
            
            {totalUsers > 0 && (
              <div className="relative">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-[#2153D3] to-[#FF9243] rounded-lg blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse"></div>
                <div className="relative px-4 sm:px-6 py-3 bg-white rounded-lg leading-none flex items-center">
                  <span className="flex items-center space-x-2">
                    <span className="text-slate-500">Total Users</span>
                    <span className="bg-gradient-to-r from-[#2153D3] to-[#FF9243] bg-clip-text text-transparent text-lg font-bold">
                      {totalUsers.toLocaleString()}
                    </span>
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile View - Search and Total Users */}
        <div className="flex flex-col md:hidden gap-3 w-full">
          <div className={`relative transition-all duration-500 ease-in-out w-full`}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder="Search users..."
              className={`w-full px-4 py-2 rounded-full border-2 transition-all duration-500 ease-in-out ${
                isFocused
                  ? 'border-blue-500 shadow-lg'
                  : searchQuery
                  ? 'border-blue-300 shadow-md'
                  : 'border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-blue-300`}
            />
          </div>
          
          {totalUsers > 0 && (
            <div className="relative w-full">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-[#2153D3] to-[#FF9243] rounded-lg blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse"></div>
              <div className="relative px-4 py-3 bg-white rounded-lg leading-none flex items-center justify-center">
                <span className="flex items-center space-x-2">
                  <span className="text-slate-500">Total Users</span>
                  <span className="bg-gradient-to-r from-[#2153D3] to-[#FF9243] bg-clip-text text-transparent text-lg font-bold">
                    {totalUsers.toLocaleString()}
                  </span>
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] bg-white rounded-lg shadow-sm">
          <div className="flex flex-col items-center space-y-4">
            <div className="relative">
              <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-[#2153D3] animate-spin"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="h-16 w-16 rounded-full border-t-4 border-b-4 border-[#FF9243] animate-spin animate-delay-150"></div>
              </div>
            </div>
            <div className="text-center space-y-3">
              <h2 className="text-xl font-semibold bg-gradient-to-r from-[#2153D3] to-[#FF9243] bg-clip-text text-transparent">
                Loading Analytics
              </h2>
              <p className="text-gray-500 max-w-sm">
                We're gathering all the important insights about your users. This might take a moment...
              </p>
            </div>
          </div>
        </div>
      ) : userData.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-6 py-3 border-b text-left">#</th>
                <th className="px-6 py-3 border-b text-left">Name</th>
                <th className="px-6 py-3 border-b text-left">Email</th>
                <th className="px-6 py-3 border-b text-center">Country</th>
                <th className="px-6 py-3 border-b text-center">Joined</th>
                <th className="px-6 py-3 border-b text-center">Status</th>
                <th className="px-6 py-3 border-b text-center">Auth</th>
                <th className="px-6 py-3 border-b text-center">Student</th>
                <th className="px-6 py-3 border-b text-center">Plan</th>
                <th className="px-6 py-3 border-b text-center">Valid Till</th>
                <th className="px-6 py-3 border-b text-center">Scripts</th>
                <th className="px-6 py-3 border-b text-center">Active</th>
              </tr>
            </thead>
            <tbody>
              {userData.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 border-b whitespace-nowrap">{currentPage === 1 ? index + 1 : (currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td className="px-6 py-4 border-b whitespace-nowrap">{item.name}</td>
                  <td className="px-6 py-4 border-b whitespace-nowrap">{item.email}</td>
                  <td className="px-6 py-4 border-b whitespace-nowrap text-center">{getCountryName(item.countryCode)}</td>
                  <td className="px-6 py-4 border-b whitespace-nowrap text-center">{formatDate(item.createdAt)}</td>
                  <td className={`px-6 py-4 border-b whitespace-nowrap text-center`}>
                    <span className={`inline-block px-4 py-1 rounded-full ${getStatusColor(item.isVerified)}`}>
                      {item.isVerified ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 border-b whitespace-nowrap text-center">
                    <span className={`inline-block px-4 py-1 rounded-full ${getAuthColor(item.loginType)}`}>
                      {(item.loginType || "").charAt(0).toUpperCase() + (item.loginType || "").slice(1).toLowerCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 border-b whitespace-nowrap text-center">{item.isStudent ? "Yes" : "No"}</td>
                  <td className={`px-6 py-4 border-b whitespace-nowrap text-center`}>
                    <span className={`inline-block px-4 py-1 rounded-full ${getPlanColor(item.subscription?.plan)}`}>
                      {item.subscription?.plan || ""}
                    </span>
                  </td>
                  <td className={`px-6 py-4 border-b whitespace-nowrap text-center`}>
                    <span className={`inline-block px-4 py-1 rounded-full ${getValidTillColor(item.subscription?.endDate)}`}>
                      {item.subscription?.endDate ? formatDate(item.subscription.endDate) : ""}
                    </span>
                  </td>
                  <td className={`px-6 py-4 border-b whitespace-nowrap text-center`}>
                    <span className={`inline-block px-4 py-1 rounded-full ${getScriptColor(item.totalScripts)}`}>
                      {item.totalScripts || 0}
                    </span>
                  </td>
                  <td className={`px-6 py-4 border-b whitespace-nowrap text-center`}>
                    <span className={`inline-block px-4 py-1 rounded-full ${getScriptColor(item.activeScripts)}`}>
                      {item.activeScripts || 0}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          <div className="flex justify-center mt-4 gap-2">
            {renderPaginationButtons()}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center min-h-[400px] bg-white rounded-lg shadow-sm">
          <div className="text-center space-y-3">
            <h2 className="text-xl font-semibold text-gray-800">No Data Available</h2>
            <p className="text-gray-500">There are no users to display at the moment.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin; 